import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { preloadAllContent } from "@/composables/useContent";
import api from "@/services/api";

import IPTVServicePage from "../pages/IPTVServicePage.vue";
import LoginPage from "../pages/LoginPage.vue";
import RegisterPage from "../pages/RegisterPage.vue";
import ChangePasswordPage from "../pages/ChangePasswordPage.vue";
import ForgotPasswordPage from "../pages/ForgotPasswordPage.vue";
import DashboardPage from "../pages/DashboardPage.vue";
import AdminDashboardPage from "../pages/admin/AdminDashboardPage.vue";
import ContentListPage from "../pages/admin/ContentListPage.vue";
import ContentEditorPage from "../pages/admin/ContentEditorPage.vue";
import PricingPage from "../pages/PricingPage.vue";
import ChannelsPage from "../pages/ChannelsPage.vue";
import FaqPage from "../pages/FaqPage.vue";
import AboutPage from "../pages/AboutPage.vue";
import ContactPage from "../pages/ContactPage.vue";
import WorldCupPage from "../pages/WorldCupPage.vue";
import BlogPage from "../pages/BlogPage.vue";
import BlogPostPage from "../pages/BlogPostPage.vue";
import BlogListPage from "../pages/admin/BlogListPage.vue";
import BlogEditorPage from "../pages/admin/BlogEditorPage.vue";
import PaymentSuccessPage from "../pages/PaymentSuccessPage.vue";
import PaymentCancelPage from "../pages/PaymentCancelPage.vue";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage.vue";
import TermsConditionsPage from "../pages/TermsConditionsPage.vue";
import RefundPolicyPage from "../pages/RefundPolicyPage.vue";
import InstallationGuidePage from "../pages/InstallationGuidePage.vue";
import ErrorPage from "../pages/ErrorPage.vue";

const routes: Array<RouteRecordRaw> = [
  { path: "/", name: "IPTVServicePage", component: IPTVServicePage },
  { path: "/iptv-service", redirect: "/" },
  { path: "/login", name: "LoginPage", component: LoginPage },
  { path: "/register", name: "RegisterPage", component: RegisterPage },
  { path: "/change-password", name: "ChangePasswordPage", component: ChangePasswordPage },
  { path: "/forgot-password", name: "ForgotPasswordPage", component: ForgotPasswordPage },
  {
    path: "/dashboard",
    name: "DashboardPage",
    component: DashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/admin",
    name: "AdminDashboardPage",
    component: AdminDashboardPage,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin/content",
    name: "ContentListPage",
    component: ContentListPage,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin/content/:section",
    name: "ContentEditorPage",
    component: ContentEditorPage,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  { path: "/world-cup-2026",   name: "WorldCupPage",        component: WorldCupPage        },
  { path: "/blog",             name: "BlogPage",            component: BlogPage            },
  { path: "/blog/:slug",       name: "BlogPostPage",        component: BlogPostPage        },
  {
    path: "/admin/blog",
    name: "BlogListPage",
    component: BlogListPage,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin/blog/:id",
    name: "BlogEditorPage",
    component: BlogEditorPage,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  { path: "/pricing",          name: "PricingPage",         component: PricingPage         },
  { path: "/channels",         name: "ChannelsPage",        component: ChannelsPage        },
  { path: "/faq",              name: "FaqPage",             component: FaqPage             },
  { path: "/about-us",         name: "AboutPage",           component: AboutPage           },
  { path: "/contact-us",       name: "ContactPage",         component: ContactPage         },
  { path: "/payment/success",  name: "PaymentSuccessPage",  component: PaymentSuccessPage  },
  { path: "/payment/cancel",   name: "PaymentCancelPage",   component: PaymentCancelPage   },
  { path: "/privacy-policy",   name: "PrivacyPolicyPage",   component: PrivacyPolicyPage   },
  { path: "/terms-conditions", name: "TermsConditionsPage", component: TermsConditionsPage },
  { path: "/refund-policy",    name: "RefundPolicyPage",    component: RefundPolicyPage    },
  { path: "/installation-guide", name: "InstallationGuidePage", component: InstallationGuidePage },
  { path: "/:pathMatch(.*)*",  name: "ErrorPage",            component: ErrorPage           },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
  linkExactActiveClass: "active",
  scrollBehavior() {
    return { top: 0, behavior: "smooth" };
  },
});

let contentPreloaded = false;

router.beforeEach(async (to) => {
  // Warm the content cache before the landing page renders so all
  // useContent() calls initialize synchronously with no placeholder flash.
  if (!contentPreloaded && to.path === "/") {
    contentPreloaded = true;
    await preloadAllContent();
  }

  const authStore = useAuthStore();
  if (to.meta.requiresAuth && !authStore.isLoggedIn) {
    return { path: "/login", query: { redirect: to.fullPath } };
  }
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    return { path: "/" };
  }
});

// Lightweight, fire-and-forget page-view tracking. Skips admin pages.
// document.referrer is set at full page load and stays the original external
// source across SPA navigations, so it attributes traffic to its true source.
router.afterEach((to) => {
  if (to.path.startsWith("/admin")) return;
  try {
    const body = JSON.stringify({ path: to.path, referrer: document.referrer || "" });
    const url = `${api.defaults.baseURL ?? ""}/api/v1/track`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      api.post("/api/v1/track", { path: to.path, referrer: document.referrer || "" }).catch(() => {
        /* ignore tracking errors */
      });
    }
  } catch {
    /* tracking must never break navigation */
  }
});

export default router;
